import { ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { NoxusEmptyBlock } from "./UI/NoxusEmptyBlock";
import { Ban } from "lucide-react";
import { NoxusLoadingScreen } from "./UI/NoxusLoadingScreen";
import { EnvService } from "@/services/Environment/Environment";
import { usePermissions } from "@/hooks/usePermissions";
import { useFirstAllowedRoute } from "@/hooks/useFirstAllowedRoute";
import {
  ORG_PERMISSIONS,
  P,
  WORKSPACE_PERMISSIONS,
} from "@/constants/permissions";

// Anyone with any resource permission can open the artifact's detail
// page in read-only mode; the editor disables save/run inside per-control
// guards. Page-level gating only blocks users who have *zero* resource
// access in the workspace.
const RESOURCE_VIEW_PERMS = [
  P.RESOURCE_READ,
  P.RESOURCE_EDIT,
  P.RESOURCE_RUN,
  P.RESOURCE_DELETE,
];

/* We only show pages that have special permission. Omission = fully allowed */

export const PAGES_AUTHORIZATION = [
  {
    href: "/templates",
    requiredPermissions: [WORKSPACE_PERMISSIONS.EDIT],
    level: "group",
  },
  {
    href: "/data-hub",
    requiredPermissions: RESOURCE_VIEW_PERMS,
    level: "group",
  },
  {
    href: "/settings/administration/billing",
    requiredPermissions: [ORG_PERMISSIONS.ORG.BILLING],
    level: "tenant",
  },
  {
    href: "/settings/organization/plans",
    requiredPermissions: [ORG_PERMISSIONS.ORG.BILLING],
    level: "tenant",
  },
  {
    href: "/settings/platform/models",
    requiredPermissions: [ORG_PERMISSIONS.PROVIDERS.MANAGE],
    level: "tenant",
  },
  {
    href: "/settings/observability/analytics",
    requiredPermissions: [ORG_PERMISSIONS.ORG.ANALYTICS],
    level: "tenant",
  },
  {
    href: "/settings/administration/users",
    requiredPermissions: [ORG_PERMISSIONS.USERS.READ],
    level: "tenant",
  },
  {
    href: "/settings/administration/workspaces",
    requiredPermissions: [ORG_PERMISSIONS.WORKSPACE.READ],
    level: "tenant",
  },
  {
    href: "/settings/administration/roles",
    requiredPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
    level: "tenant",
  },
  {
    href: "/settings/organization/plugins",
    requiredPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
    level: "tenant",
  },
  {
    href: "/settings/organization/remote-access",
    requiredPermissions: [ORG_PERMISSIONS.ORG.ADMIN],
    level: "tenant",
  },
  {
    href: "/assistant/{id}",
    requiredPermissions: RESOURCE_VIEW_PERMS,
    level: "group",
  },
  {
    href: "/flows/{id}",
    requiredPermissions: RESOURCE_VIEW_PERMS,
    level: "group",
  },
] as const;

const Authorization = ({
  children,
  requiredPermissions,
  level = "tenant",
  requireAll = false,
  requiredEnvVars,
  orTenantPermissions,
}: {
  children: ReactNode;
  requiredPermissions?: string[];
  level?: "tenant" | "group";
  requireAll?: boolean;
  requiredEnvVars?: {
    env_var: string;
    value: string;
  }[];
  // Organization permissions that also open the page, whatever the workspace
  // role, for a page whose backend carries the same fallback. Matches the
  // section gates in pageSections.ts.
  orTenantPermissions?: string[];
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();
  const { route: firstAllowedRoute, isLoading: isLoadingRoute } =
    useFirstAllowedRoute();

  const isForbiddenPermissions =
    requiredPermissions &&
    requiredPermissions.length > 0 &&
    (requireAll
      ? !hasAllPermissions(requiredPermissions, level)
      : !hasAnyPermission(requiredPermissions, level)) &&
    !(
      orTenantPermissions &&
      orTenantPermissions.length > 0 &&
      hasAnyPermission(orTenantPermissions, "tenant")
    );

  const isForbiddenEnvVars =
    requiredEnvVars &&
    requiredEnvVars.some((envVar) => {
      const envVarValue = EnvService.getVar(envVar.env_var);
      return envVarValue !== envVar.value;
    });

  const isForbidden = isForbiddenPermissions || isForbiddenEnvVars;

  // Render children optimistically while permissions load to avoid layout shift.
  // Once loaded, isForbidden will kick in if the user lacks access.
  if (isLoading) {
    // Only block on the root redirect case — other pages render optimistically
    if (pathname === "/") return <NoxusLoadingScreen />;
    return <>{children}</>;
  }

  if (pathname === "/" && isLoadingRoute) return <NoxusLoadingScreen />;

  if (pathname === "/" && firstAllowedRoute !== "/") {
    router.replace(firstAllowedRoute);
    return <NoxusLoadingScreen />;
  }

  // If user role doesn't match
  if (isForbidden) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <NoxusEmptyBlock
          title="Access Denied"
          description="You don’t have permission to access this page. Please contact your organization’s administrator for assistance."
          icon={Ban}
          button="Go to home"
          onClick={() => router.push(firstAllowedRoute)}
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default Authorization;
